import { Suspense, lazy } from 'react';
import {
  Outlet,
  createRootRouteWithContext,
  useRouter,
} from '@tanstack/react-router';
import type { ErrorComponentProps } from '@tanstack/react-router';
import type { RouterContext } from '@/router';
import { StatusPage } from '@/components/status-page';
import { isDev } from '@/lib/env';
import { ImpersonationBanner } from '@/features/auth/impersonation/banner';

/**
 * Loaded only in development, and only when the browser asks for it: the devtools are a
 * separate chunk, so a production build never pays for them.
 */
const Devtools = lazy(async () => {
  const [{ TanStackRouterDevtools }, { ReactQueryDevtools }] =
    await Promise.all([
      import('@tanstack/react-router-devtools'),
      import('@tanstack/react-query-devtools'),
    ]);
  return {
    default: () => (
      <>
        <TanStackRouterDevtools position="bottom-right" />
        <ReactQueryDevtools
          buttonPosition="bottom-left"
          initialIsOpen={false}
        />
      </>
    ),
  };
});

const RootLayout = () => (
  <>
    <ImpersonationBanner />
    <Outlet />
    {isDev ? (
      <Suspense fallback={null}>
        <Devtools />
      </Suspense>
    ) : null}
  </>
);

const NotFoundPage = () => <StatusPage status={404} />;

const RootErrorPage = ({ error, reset }: ErrorComponentProps) => {
  const router = useRouter();
  return (
    <StatusPage error={error} onRetry={() => router.invalidate().then(reset)} />
  );
};

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
  notFoundComponent: NotFoundPage,
  errorComponent: RootErrorPage,
});
